import { forwardRef, useId } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';
import { AlertCircle } from 'lucide-react';
import { cn } from '../../lib/cn';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  /** Mensaje de error humano en español; activa el estado de error. */
  error?: string;
  /** Texto de ayuda bajo el campo (se oculta si hay error). */
  hint?: string;
  leadingIcon?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, error, hint, leadingIcon, id, className, required, ...rest },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const errorId = `${inputId}-error`;
  const hintId = `${inputId}-hint`;
  const hasError = Boolean(error);

  return (
    <div className="flex flex-col gap-1">
      <label
        htmlFor={inputId}
        className="font-sans text-sm font-medium text-content"
      >
        {label}
        {required && (
          <span className="text-error" aria-hidden="true">
            {' '}*
          </span>
        )}
      </label>

      <div className="relative flex items-center">
        {leadingIcon && (
          <span
            className="pointer-events-none absolute left-3 text-content-muted"
            aria-hidden="true"
          >
            {leadingIcon}
          </span>
        )}
        <input
          ref={ref}
          id={inputId}
          required={required}
          aria-invalid={hasError || undefined}
          aria-describedby={hasError ? errorId : hint ? hintId : undefined}
          className={cn(
            'min-h-touch w-full rounded-md bg-surface-raised px-3 text-base text-content',
            'placeholder:text-content-muted',
            'border transition-colors duration-fast',
            'focus-visible:outline-none focus-visible:shadow-focus',
            hasError
              ? 'border-error focus-visible:shadow-focus'
              : 'border-border-strong',
            leadingIcon ? 'pl-10' : null,
            className,
          )}
          {...rest}
        />
      </div>

      {hasError ? (
        <p
          id={errorId}
          role="alert"
          className="flex items-center gap-1 text-sm text-error"
        >
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="text-sm text-content-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
});
